import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, XCircle, MusicNote, FilePdf, Clock } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatAnnoLabel = (y) => `${y}/${String(y + 1).slice(2)}`;

export default function ConfirmLessonPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [choices, setChoices] = useState({}); // { [lesson_key]: true|false|null }
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [acceptedRegolamento, setAcceptedRegolamento] = useState(false);
  const [acceptedListino, setAcceptedListino] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/confirm/${token}`)
      .then(res => {
        setData(res.data);
        if (res.data.status === 'responded') {
          setDone(true);
        }
      })
      .catch(err => setError(err.response?.data?.detail || 'Link non valido'))
      .finally(() => setLoading(false));
  }, [token]);

  const [bonificoFile, setBonificoFile] = useState(null);
  const [bonificoUploading, setBonificoUploading] = useState(false);
  const [bonificoUploaded, setBonificoUploaded] = useState(false);

  useEffect(() => {
    if (data?.bonifico_uploaded) setBonificoUploaded(true);
  }, [data]);

  const handleUploadBonifico = async () => {
    if (!bonificoFile) { toast.error('Seleziona un file'); return; }
    setBonificoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', bonificoFile);
      await axios.post(`${API}/public/confirm/${token}/bonifico`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Contabile caricata con successo');
      setBonificoUploaded(true);
      setBonificoFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento');
    } finally { setBonificoUploading(false); }
  };

  const setChoice = (key, val) => setChoices(prev => ({ ...prev, [key]: val }));

  const allLessonsDecided = data?.lessons?.length > 0 && data.lessons.every(l => ['yes', 'wait', 'no'].includes(choices[l.lesson_key]));
  const anyYes = data?.lessons?.some(l => choices[l.lesson_key] === 'yes');
  const allDocsAccepted = acceptedRegolamento && acceptedListino;
  // I PDF sono obbligatori solo se almeno una lezione è confermata
  const canSubmit = allLessonsDecided && (!anyYes || allDocsAccepted);

  const handleSubmit = async () => {
    if (!allLessonsDecided) { toast.error('Indica una scelta per ogni lezione'); return; }
    if (anyYes && !allDocsAccepted) { toast.error('Devi confermare di aver letto il regolamento e il listino prezzi'); return; }
    setSubmitting(true);
    try {
      const lesson_choices = data.lessons.map(l => ({ lesson_key: l.lesson_key, choice: choices[l.lesson_key] }));
      await axios.post(`${API}/public/confirm/${token}`, {
        lesson_choices,
        alternative_1: '',
        alternative_2: '',
      });
      setDone(true);
      toast.success('Risposta inviata, grazie!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"><p className="text-[#52525B]">Caricamento...</p></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
      <div className="max-w-md text-center bg-white border border-[#E4E4E7] rounded-sm p-8">
        <XCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h1 className="text-xl font-bold mb-2">Link non valido</h1>
        <p className="text-[#52525B] text-sm">{error}</p>
      </div>
    </div>
  );

  const renderDoneMessage = () => {
    const lessons = data.lessons || [];
    const hasAnyYes = lessons.some(l => {
      const choice = l.choice || choices[l.lesson_key] || (l.confirmed === true ? 'yes' : null);
      return choice === 'yes';
    });
    // Per-lesson messages
    return (
      <div className="space-y-4" data-testid="confirm-done">
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 text-center">
          <CheckCircle size={48} className="mx-auto text-[#2D2A4A] mb-3" weight="fill" />
          <h2 className="text-xl font-bold">Risposta registrata</h2>
          <p className="text-sm text-[#52525B] mt-2">Riepilogo della tua scelta per ciascun corso:</p>
        </div>
        <div className="space-y-3">
          {lessons.map(l => {
            // Determina la choice: dal record DB (l.choice) o fallback dallo stato locale
            const choice = l.choice || choices[l.lesson_key] || (l.confirmed === true ? 'yes' : l.confirmed === false ? 'no' : 'no');
            const borderClass = choice === 'yes' ? 'border-green-200' : choice === 'wait' ? 'border-amber-200' : 'border-red-200';
            return (
              <div key={l.lesson_key} className={`bg-white border-2 rounded-sm p-4 ${borderClass}`} data-testid={`done-lesson-${l.lesson_key}`}>
                <div className="flex items-baseline gap-2 flex-wrap mb-2 text-sm">
                  <span className="font-semibold">{l.day_label}</span>
                  <span className="font-['Work_Sans']">{l.start_time}-{l.end_time}</span>
                  <span className="text-[#52525B]">con</span>
                  <span className="font-semibold uppercase">{l.teacher_name}</span>
                  {l.instrument && <span className="text-[#52525B]">({l.instrument})</span>}
                  <span className="ml-auto">
                    {choice === 'yes' && <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded"><CheckCircle size={14} weight="fill" /> CONFERMATA</span>}
                    {choice === 'wait' && <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded"><Clock size={14} weight="fill" /> IN ATTESA 2° SLOT</span>}
                    {choice === 'no' && <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded"><XCircle size={14} weight="fill" /> NON CONFERMATA</span>}
                  </span>
                </div>
                {choice === 'yes' && (
                  <p className="text-xs text-green-800 bg-green-50 p-2 rounded-sm">
                    <strong>Ricordiamo che</strong> riceverai le informazioni per eseguire <strong>il pagamento</strong>, che è requisito essenziale perché l&apos;iscrizione sia <strong>CONFERMATA</strong>.
                  </p>
                )}
                {choice === 'wait' && (
                  <p className="text-xs text-amber-800 bg-amber-50 p-2 rounded-sm">
                    Mi riservo di aspettare la seconda finestra di prelazione autunnale (31/08/26 – 04/09/26).<br />
                    La tua Lezione, per il momento, non è confermata e per ora non viene assegnata a nessun altro alunno.<br />
                    Restiamo a tua disposizione: nel caso cambiassi idea.
                  </p>
                )}
                {choice === 'no' && (
                  <p className="text-xs text-red-800 bg-red-50 p-2 rounded-sm">
                    Non intendo proseguire gli studi.
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {hasAnyYes && (
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="bonifico-section">
            <div className="flex items-center gap-2 mb-2">
              <FilePdf size={20} className="text-[#2D2A4A]" weight="duotone" />
              <h3 className="text-base font-bold font-['Work_Sans']">Carica la contabile del bonifico</h3>
            </div>
            <p className="text-xs text-[#52525B] mb-3">
              Una volta effettuato il pagamento, carica qui la contabile (PDF, JPG o PNG). Max 10MB.
            </p>
            {bonificoUploaded ? (
              <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-sm text-green-800 flex items-center gap-2" data-testid="bonifico-uploaded">
                <CheckCircle size={20} weight="fill" />
                <span>Contabile ricevuta. Verrà verificata a breve dalla segreteria.</span>
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/jpg"
                  onChange={(e) => setBonificoFile(e.target.files?.[0] || null)}
                  className="w-full text-xs border border-[#E4E4E7] rounded-sm p-2 file:mr-3 file:rounded-sm file:border-0 file:bg-[#2D2A4A] file:text-white file:px-3 file:py-1.5 file:text-xs file:cursor-pointer"
                  data-testid="bonifico-file-input"
                />
                <Button
                  onClick={handleUploadBonifico}
                  disabled={!bonificoFile || bonificoUploading}
                  className="w-full bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm"
                  data-testid="bonifico-upload-btn"
                >
                  {bonificoUploading ? 'Caricamento...' : 'Carica Contabile'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          {data.logo_url ? (
            <img src={`${BACKEND_URL}${data.logo_url}`} alt={data.company_name || 'Logo'} className="mx-auto mb-3 max-h-24 w-auto" data-testid="confirm-page-logo" />
          ) : (
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#2D2A4A] text-white mb-3">
              <MusicNote size={28} weight="fill" />
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans']" data-testid="confirm-page-title">Conferma Iscrizione {formatAnnoLabel(data.target_year)}</h1>
          {data.company_name && <p className="text-[#52525B] text-sm mt-1">{data.company_name}</p>}
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 mb-6">
          <p className="text-sm mb-1">Gentile <span className="font-semibold uppercase">{data.student_name}</span>,</p>
          <p className="text-sm text-[#52525B] mb-1">
            Le tue lezioni dell&apos;anno <span className="font-semibold">{formatAnnoLabel(data.source_year)}</span>:
          </p>
          {!done && (
            <p className="text-xs text-[#52525B] mb-4">
              Per ogni lezione, scegli se confermarla o no per l&apos;anno <span className="font-semibold">{formatAnnoLabel(data.target_year)}</span>.
            </p>
          )}

          {data.lessons.length === 0 ? (
            <p className="text-sm text-[#52525B]">Nessuna lezione registrata.</p>
          ) : !done && (
            <div className="space-y-3">
              {data.lessons.map((l) => {
                const ch = choices[l.lesson_key];
                return (
                <div key={l.lesson_key} className="border border-[#E4E4E7] rounded-sm p-3" data-testid={`lesson-row-${l.lesson_key}`}>
                  <div className="flex items-baseline gap-2 flex-wrap mb-2 text-sm">
                    <span className="font-semibold">{l.day_label}</span>
                    <span className="font-['Work_Sans']">{l.start_time}-{l.end_time}</span>
                    <span className="text-[#52525B]">con</span>
                    <span className="font-semibold uppercase">{l.teacher_name}</span>
                    {l.instrument && <span className="text-[#52525B]">({l.instrument})</span>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <button
                      onClick={() => setChoice(l.lesson_key, 'yes')}
                      className={`p-2.5 rounded-sm border-2 transition-all text-xs sm:text-sm font-semibold ${ch === 'yes' ? 'border-green-600 bg-green-50 text-green-700' : 'border-[#E4E4E7] hover:border-green-300 text-[#52525B]'}`}
                      data-testid={`confirm-yes-${l.lesson_key}`}
                    >
                      <CheckCircle size={18} className="inline mr-1" weight={ch === 'yes' ? 'fill' : 'regular'} />
                      SÌ, confermo
                    </button>
                    <button
                      onClick={() => setChoice(l.lesson_key, 'wait')}
                      className={`p-2.5 rounded-sm border-2 transition-all text-xs sm:text-sm font-semibold ${ch === 'wait' ? 'border-amber-600 bg-amber-50 text-amber-700' : 'border-[#E4E4E7] hover:border-amber-300 text-[#52525B]'}`}
                      data-testid={`confirm-wait-${l.lesson_key}`}
                    >
                      <Clock size={18} className="inline mr-1" weight={ch === 'wait' ? 'fill' : 'regular'} />
                      ATTENDO 2° SLOT
                    </button>
                    <button
                      onClick={() => setChoice(l.lesson_key, 'no')}
                      className={`p-2.5 rounded-sm border-2 transition-all text-xs sm:text-sm font-semibold ${ch === 'no' ? 'border-red-600 bg-red-50 text-red-700' : 'border-[#E4E4E7] hover:border-red-300 text-[#52525B]'}`}
                      data-testid={`confirm-no-${l.lesson_key}`}
                    >
                      <XCircle size={18} className="inline mr-1" weight={ch === 'no' ? 'fill' : 'regular'} />
                      NON CONFERMO
                    </button>
                  </div>
                  {ch === 'yes' && (
                    <p className="mt-2 text-xs text-green-800 bg-green-50 p-2 rounded-sm" data-testid={`msg-yes-${l.lesson_key}`}>
                      <strong>Ricordiamo che</strong> riceverai le informazioni per eseguire <strong>il pagamento</strong>, che è requisito essenziale perché l&apos;iscrizione sia <strong>CONFERMATA</strong>.
                    </p>
                  )}
                  {ch === 'wait' && (
                    <p className="mt-2 text-xs text-amber-800 bg-amber-50 p-2 rounded-sm" data-testid={`msg-wait-${l.lesson_key}`}>
                      Mi riservo di aspettare la seconda finestra di prelazione autunnale (31/08/26 – 04/09/26).<br />
                      La tua Lezione, per il momento, non è confermata e per ora non viene assegnata a nessun altro alunno.<br />
                      Ricordiamo che dal 31 agosto al 04 settembre sarà possibile scegliere, sempre con prelazione, una Lezione fra gli orari disponibili.<br />
                      Restiamo a tua disposizione: nel caso cambiassi idea.
                    </p>
                  )}
                  {ch === 'no' && (
                    <p className="mt-2 text-xs text-red-800 bg-red-50 p-2 rounded-sm" data-testid={`msg-no-${l.lesson_key}`}>
                      Non intendo proseguire gli studi.
                    </p>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {done ? renderDoneMessage() : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 space-y-4">
            {data.lessons.length > 0 && anyYes && (
              <div className="space-y-3" data-testid="docs-section">
                <p className="text-sm font-semibold text-[#3F3F46]">Documenti da visionare per l&apos;anno {data.target_year}/{String(data.target_year + 1).slice(2)}:</p>
                {[
                  { url: data.regolamento_url, label: 'Regolamento Accademia', state: acceptedRegolamento, setter: setAcceptedRegolamento, key: 'regolamento' },
                  { url: data.listino_url, label: 'Listino Prezzi', state: acceptedListino, setter: setAcceptedListino, key: 'listino' },
                ].map(d => (
                  <div key={d.key} className="border border-[#E4E4E7] rounded-sm p-3 space-y-2" data-testid={`doc-${d.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <FilePdf size={20} className="text-red-600" weight="duotone" />
                        {d.label}
                      </span>
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-[#2D2A4A] underline hover:no-underline" data-testid={`view-${d.key}-btn`}>
                        Visualizza PDF
                      </a>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox checked={d.state} onCheckedChange={(v) => d.setter(!!v)} data-testid={`accept-${d.key}-checkbox`} className="mt-0.5" />
                      <span className="text-xs text-[#52525B]">Confermo di aver letto il <strong>{d.label}</strong>.</span>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {!allLessonsDecided && data.lessons.length > 0 && (
              <p className="text-xs text-[#A1A1AA] italic">Scegli una opzione per ogni lezione qui sopra per continuare.</p>
            )}
            {allLessonsDecided && anyYes && !allDocsAccepted && data.lessons.length > 0 && (
              <p className="text-xs text-[#A1A1AA] italic">Visualizza i due PDF e spunta le caselle qui sopra per attivare l&apos;invio.</p>
            )}

            <div className="pt-2">
              <Button onClick={handleSubmit} disabled={submitting || !canSubmit} className="w-full bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="confirm-submit-btn">
                {submitting ? 'Invio...' : 'Invia Risposta'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
